import { notifications } from '@mantine/notifications';
import { find } from 'remeda';
import { userConfigurationsQueryKey } from '../../../../../hooks/queries/keys';
import { STEP_TO_ACTIVE_MAP } from '../../constants';
import type { CreateSessionDependencies } from './types';
import { transformExperienceFormData } from '../../utils/experienceForm/transform';
import { buildScenarioConfigurationPayload } from '../../utils/scenarioForm/transform';
import { type CreateConfigurationRequest } from '@types';
import { buildExperiencePayload } from './buildExperiencePayload';

export function createSessionHandler({
  currentUserId,
  selectedPatientId,
  selectedDeviceId,
  selectedSessionType,
  experienceForm,
  scenarioForm,
  validateAll,
  invalidStepNotifications,
  setActiveStep,
  navigate,
  queryClient,
  allDevices,
}: CreateSessionDependencies) {
  return async () => {
    if (!currentUserId) {
      notifications.show({
        color: 'red',
        title: 'Errore',
        message: 'Utente non autenticato',
      });
      return;
    }

    if (!selectedPatientId) {
      notifications.show(invalidStepNotifications.patient);
      setActiveStep(STEP_TO_ACTIVE_MAP.patient ?? 0);
      return;
    }

    if (!selectedDeviceId) {
      notifications.show(invalidStepNotifications.device);
      setActiveStep(STEP_TO_ACTIVE_MAP.device ?? 1);
      return;
    }

    if (!selectedSessionType) {
      notifications.show(invalidStepNotifications.sessionType);
      setActiveStep(STEP_TO_ACTIVE_MAP.sessionType ?? 2);
      return;
    }

    const userId = Number(currentUserId);
    const patientId = Number(selectedPatientId);
    const deviceId = Number(selectedDeviceId);

    if (Number.isNaN(userId)) {
      notifications.show({
        color: 'red',
        title: 'Errore',
        message: 'Identificativo utente non valido',
      });
      return;
    }

    if (Number.isNaN(patientId)) {
      notifications.show({
        color: 'red',
        title: 'Errore',
        message: 'Identificativo utente non valido',
      });
      return;
    }

    if (Number.isNaN(deviceId)) {
      notifications.show({
        color: 'red',
        title: 'Errore',
        message: 'Identificativo dispositivo non valido',
      });
      return;
    }

    // Find the sensor connected to the selected device
    const connectedSensor = find(
      allDevices,
      d =>
        d.type === 'smartwatch' &&
        (d.connected_to === deviceId ||
          d.connected_to === deviceId.toString() ||
          String(d.connected_to) === String(deviceId))
    );

    const sensorId = connectedSensor?.id;

    const validationResult = await validateAll();

    if (!validationResult.isValid) {
      const [firstInvalidStep] = validationResult.invalidSteps;

      if (firstInvalidStep) {
        const notification = invalidStepNotifications[firstInvalidStep];

        setActiveStep(STEP_TO_ACTIVE_MAP[firstInvalidStep] ?? 0);

        if (firstInvalidStep === 'configuration') {
          if (selectedSessionType === 'scenario') {
            void scenarioForm.trigger();
          } else {
            void experienceForm.trigger();
          }
        }

        if (notification) {
          notifications.show(notification);
        }
      }

      return;
    }

    if (selectedSessionType === 'scenario') {
      try {
        const scenarioValues = scenarioForm.getValues();
        const payload = buildScenarioConfigurationPayload({
          formValues: scenarioValues,
          patientId: selectedPatientId,
          deviceId: selectedDeviceId,
          sensorId: sensorId ? String(sensorId) : undefined,
        });

        const scenarioResponse = await fetch(
          `${import.meta.env.VITE_API_URL}/services/v1/configurations`,
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-type': 'application/json; charset=UTF-8',
            },
            body: JSON.stringify(payload),
          }
        );

        if (!scenarioResponse.ok) {
          const errorText = await scenarioResponse.text();
          throw new Error(`HTTP ${scenarioResponse.status}: ${errorText}`);
        }

        notifications.show({
          color: 'green',
          title: 'Successo',
          message: 'Sessione creata con successo!',
        });

        await queryClient.invalidateQueries({
          queryKey: userConfigurationsQueryKey(String(userId)),
        });
        navigate('/sessions');
      } catch (error) {
        notifications.show({
          color: 'red',
          title: 'Errore',
          message:
            error instanceof Error
              ? error.message
              : 'Errore durante il salvataggio dello scenario',
        });
      }

      return;
    }

    try {
      const formValues = experienceForm.getValues();

      if (!formValues) {
        throw new Error('Impossibile recuperare i valori del form');
      }

      const transformedConfig = transformExperienceFormData(formValues);

      const payload: CreateConfigurationRequest = buildExperiencePayload({
        formValues,
        transformedConfig: JSON.stringify(transformedConfig),
        deviceId,
        patientId,
        creatorUserId: userId,
        sensorId,
      });

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/services/v1/configurations`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-type': 'application/json; charset=UTF-8',
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      notifications.show({
        color: 'green',
        title: 'Successo',
        message: 'Sessione creata con successo!',
      });

      await queryClient.invalidateQueries({
        queryKey: userConfigurationsQueryKey(String(userId)),
      });
      navigate('/sessions');
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Errore',
        message:
          error instanceof Error
            ? error.message
            : 'Errore durante il salvataggio della configurazione',
      });
    }
  };
}
